import React, {useCallback, useContext, useState} from 'react';
import BSModal from 'react-bootstrap/Modal';
import NodeEditLinks from './NodeEditLinks';
import {NodeUserData, NodeView} from "../../../../types/Node";
import {useRestClient} from "../../../../client/SpmRestClient";
import {useAlerts} from "../../../../utils/UserAlerts";
import {ConfirmDialogContext, SaveButton, Switch, TextInput} from "zavadil-react-common";
import {Button, Form, Stack} from "react-bootstrap";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import {DateUtilCs, NumberUtil, StringUtil} from "zavadil-ts-common";
import NodeEditContactFilters from "./NodeEditContactFilters";

//Vložte celé číslo (může být i záporné
//Vložte číslo otázky, která předchází této.

export type NodeFormProps = {
	node: NodeView;
	onSaved: (node: NodeView) => any;
	onClosed: () => void;
}

export default function NodeForm({node, onSaved, onClosed}: NodeFormProps) {
	const restClient = useRestClient();
	const userAlers = useAlerts();
	const confirmDialog = useContext(ConfirmDialogContext);
	const [data, setData] = useState<NodeUserData>(
		{
			id: node.id,
			parentId: node.parentId,
			answer: node.answer,
			answerSecondary: node.answerSecondary,
			weight: node.weight,
			text: node.text,
			textSecondary: node.textSecondary,
			createDate: node.createDate,
			changeDate: node.changeDate,
			isContact: node.isContact,
			isEnabled: node.isEnabled,
			googleSearch: node.googleSearch,
			userTrackingScore: node.userTrackingScore,
			userTrackingResult: node.userTrackingResult,
			contactTypeFilters: node.contactTypeFilters.map(
				(f => {
					return {
						contactTypeId: f.contactTypeId,
						targetGroups: [...f.targetGroups]
					};
				})
			),
			links: node.links.map(
				(l) => {
					return {
						id: l.id,
						url: l.url,
						name: l.name,
						description: l.description
					};
				}
			)
		}
	);
	const [isChanged, setIsChanged] = useState(false);
	const [isSaving, setIsSaving] = useState(false);

	const onChanged = useCallback(
		() => {
			setIsChanged(true);
			setData({...data});
		},
		[data]
	);

	const save = useCallback(
		() => {
			setIsSaving(true);
			restClient.nodes
				.save(data)
				.then(onSaved)
				.catch((e) => userAlers.err(e))
				.finally(() => setIsSaving(false));
		},
		[data, restClient, userAlers, onSaved]
	);

	const close = useCallback(
		() => {
			if (!isChanged) {
				onClosed();
				return;
			}
			confirmDialog.confirm(
				'Zavřít bez uložení?',
				'Formulář obsahuje neuložená data, která budou ztracena, pokud jej nyní uzavřete. Opravdu si přejete formulář uzavřít?',
				() => onClosed()
			);
		},
		[isChanged, confirmDialog, onClosed]
	);

	const rowClass = 'my-2';
	const col1 = 3;

	return (
		<BSModal size="lg" show={true} onHide={close}>
			<BSModal.Header closeButton className={`text-default`}>
				<BSModal.Title>Editace otázky [{node.id === null ? '?' : node.id}]</BSModal.Title>
			</BSModal.Header>
			<BSModal.Body>
				<Form onSubmit={save}>
					<Row className={`small ${rowClass}`}>
						<Col sm={col1}>Vytvořeno:</Col>
						<Col>
							{DateUtilCs.formatDateTimeForHumans(node.createDate)} {node.createdBy}
						</Col>
					</Row>

					<Row className={`small ${rowClass}`}>
						<Col sm={col1}>Aktualizováno:</Col>
						<Col>
							{DateUtilCs.formatDateTimeForHumans(node.changeDate)} {node.changedBy}
						</Col>
					</Row>

					<Row className={`small ${rowClass}`}>
						<Col md={col1}>Návštěvnost:</Col>
						<Col>
							{node.userTrackingScore} / {node.userTrackingResult} (celkem / jako
							poslední uzel)
						</Col>
					</Row>

					<Row className={rowClass}>
						<Col md={col1}>Předchozí otázka:</Col>
						<Col>
							<TextInput
								value={StringUtil.toString(data.parentId)}
								onChange={
									(str) => {
										data.parentId = NumberUtil.parseNumber(str);
										onChanged();
									}
								}
							/>
						</Col>
					</Row>
					<Row className={rowClass}>
						<Col>
							<Switch
								checked={data.isEnabled}
								onChange={
									(b) => {
										data.isEnabled = b;
										onChanged();
									}
								}
								label="Aktivní"
							/>
						</Col>
					</Row>

					<Row className={rowClass}>
						<Col md={col1}>Váha v pořadí:</Col>
						<Col md={2}>
							<TextInput
								value={StringUtil.toString(data.weight)}
								onChange={
									(str) => {
										data.weight = NumberUtil.parseNumber(str) || 0;
										onChanged();
									}
								}
							/>
						</Col>
						<Col md={10 - col1}>
								<span className="small">
									Vyšší váha znamená umístění odpovědi níže v seznamu.
								</span>
						</Col>
					</Row>

					<Row className={rowClass}>
						<Col md={col1}>Odpověď:</Col>
						<Col>
							<TextInput
								value={StringUtil.toString(data.answer)}
								onChange={
									(str) => {
										data.answer = str;
										onChanged();
									}
								}
							/>
						</Col>
					</Row>

					<Row className={rowClass}>
						<Col md={col1}>Odpověď (doplňující):</Col>
						<Col>
							<TextInput
								value={StringUtil.toString(data.answerSecondary)}
								onChange={
									(str) => {
										data.answerSecondary = str;
										onChanged();
									}
								}
							/>
						</Col>
					</Row>

					<Row className={rowClass}>
						<Col md={col1}>Text:</Col>
						<Col>
							<Form.Control
								as="textarea"
								rows={3}
								value={StringUtil.toString(data.text)}
								onChange={
									(e) => {
										data.text = e.target.value;
										onChanged();
									}
								}
							/>
						</Col>
					</Row>

					<Row className={rowClass}>
						<Col md={col1}>Text (doplňující):</Col>
						<Col>
							<Form.Control
								as="textarea"
								rows={3}
								value={StringUtil.toString(data.textSecondary)}
								onChange={
									(e) => {
										data.textSecondary = e.target.value;
										onChanged();
									}
								}
							/>
						</Col>
					</Row>

					<Row className={rowClass}>
						<Col>
							<Switch
								checked={data.isContact}
								label="Kontakt"
								onChange={
									(b) => {
										data.isContact = b;
										onChanged();
									}
								}
							/>
						</Col>
					</Row>

					{data.isContact && (
						<>
							<Row className={rowClass}>
								<Col md={col1}>Google vyhledávání:</Col>
								<Col>
									<TextInput
										value={StringUtil.toString(data.googleSearch)}
										onChange={
											(str) => {
												data.googleSearch = StringUtil.emptyToNull(str);
												onChanged();
											}
										}
									/>
								</Col>
							</Row>

							<Row className={rowClass}>
								<Col md={col1}>Odkazy:</Col>
								<Col>
									<NodeEditLinks
										links={data.links}
										onChange={
											(links) => {
												data.links = links;
												onChanged();
											}
										}
									/>
								</Col>
							</Row>

							<Row className={rowClass}>
								<Col md={col1}>Kontakty:</Col>
								<Col>
									<NodeEditContactFilters
										filters={data.contactTypeFilters}
										onChange={
											(filters) => {
												data.contactTypeFilters = filters;
												onChanged();
											}
										}
									/>
								</Col>
							</Row>
						</>
					)}

					<Stack direction="horizontal" gap={2} className="justify-content-center">
						<Button
							variant="secondary"
							onClick={close}
							className="mr-2"
							disabled={isSaving}
						>Zavřít</Button>
						<SaveButton onClick={save} loading={isSaving} disabled={!(isChanged)}>Uložit</SaveButton>
					</Stack>
				</Form>
			</BSModal.Body>
		</BSModal>
	);
}
